// ============================================================================
// visibility.ts — ЕДИНАЯ функция видимости элемента (SRP, SSoT).
// Любой элемент (кнопка/команда/поле/раздел/папка) проверяется ОДНОЙ функцией:
//   1) функциональные опции (серверные GetActiveOptions — скрытые коды);
//   2) роли (элемент доступен только перечисленным ролям);
//   3) принудительный флаг видимости (убран с формы/скрыт настройкой).
// Кэш активных опций грузится с сервера (SSoT — метаданные) и обновляется
// по событию конфигурации. НИКАКОГО хардкора видимости в компонентах.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';

let hiddenCodes = new Set<string>();
let optionsLoaded: Promise<void> | null = null;
const subscribers = new Set<() => void>();

/** Загрузка активных функциональных опций (серверный SSoT). */
export function loadVisibilityOptions(): Promise<void> {
  if (optionsLoaded) return optionsLoaded;
  optionsLoaded = getSDK().metadata
    .getActiveOptions({})
    .then((r: any) => {
      hiddenCodes = new Set(((r as any)?.hiddenCodes || []) as string[]);
      subscribers.forEach((cb) => cb());
    })
    .catch(() => {
      hiddenCodes = new Set();
    });
  return optionsLoaded;
}

/** Сброс кэша (после изменения ФО/конфигурации). */
export function resetVisibilityOptions(): void {
  hiddenCodes = new Set();
  optionsLoaded = null;
}

export function subscribeVisibility(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export interface VisibilityInput {
  /** Код элемента для функциональных опций (member_code, например 'command:create'). */
  code?: string;
  /** Коды функциональных опций, скрывающих элемент (если известны на клиенте). */
  optionCodes?: string[];
  /** Роли, которым элемент доступен (пусто = всем). */
  allowedRoles?: string[];
  /** Принудительно скрыт (убран с формы / настройка). */
  hidden?: boolean;
}

/**
 * ЕДИНАЯ проверка видимости элемента:
 * роль + функциональные опции + принудительный флаг.
 */
export function isElementVisible(input: VisibilityInput): boolean {
  if (input.hidden) return false;
  // Роли: элемент доступен только перечисленным
  if (input.allowedRoles && input.allowedRoles.length > 0) {
    const userRoles = useAuthStore.getState().user?.roles || [];
    if (!input.allowedRoles.some((r) => userRoles.includes(r))) return false;
  }
  // Функциональные опции (серверный SSoT — hiddenCodes)
  const codes = [...(input.code ? [input.code] : []), ...(input.optionCodes || [])];
  if (codes.some((c) => c && hiddenCodes.has(c))) return false;
  return true;
}
